/**
 * UI MK2 第1段 1a — Undo/Redo 用の汎用スナップショットスタック。
 *
 * 構造共有は呼び出し側（editorStore の `mutate`）が既に spread で新オブジェクトを
 * 作っているので、ここでは参照をそのまま積むだけで良い（ディープコピー不要）。
 *
 * バッチ（ドラッグ等の連続操作を 1 エントリにまとめる）: `beginBatch()` の後、
 * 最初の `push` だけが実際にスタックへ積まれ、`endBatch()` までの残りは無視される。
 * これにより pointerdown→pointermove×N→pointerup が 1 回の Undo で戻る。
 */
export class HistoryStack<T> {
  private undoStack: T[] = []
  private redoStack: T[] = []
  private batching = false
  private batchPushed = false

  constructor(private readonly limit = 100) {}

  /** 連続操作の開始。以後 `endBatch()` まで push は最初の 1 回だけ有効。 */
  beginBatch(): void {
    this.batching = true
    this.batchPushed = false
  }

  /** 連続操作の終了。 */
  endBatch(): void {
    this.batching = false
    this.batchPushed = false
  }

  /** 変更前の状態 `prev` を積む。新しい push が起きたら redo スタックは破棄される。 */
  push(prev: T): void {
    if (this.batching) {
      if (this.batchPushed) return
      this.batchPushed = true
    }
    this.undoStack.push(prev)
    if (this.undoStack.length > this.limit) this.undoStack.shift()
    this.redoStack = []
  }

  /** 元に戻す。`current` を redo 用に退避し、直前の状態を返す（無ければ undefined）。 */
  undo(current: T): T | undefined {
    const prev = this.undoStack.pop()
    if (prev === undefined) return undefined
    this.redoStack.push(current)
    if (this.redoStack.length > this.limit) this.redoStack.shift()
    return prev
  }

  /** やり直す。`current` を undo 用に退避し、次の状態を返す（無ければ undefined）。 */
  redo(current: T): T | undefined {
    const next = this.redoStack.pop()
    if (next === undefined) return undefined
    this.undoStack.push(current)
    if (this.undoStack.length > this.limit) this.undoStack.shift()
    return next
  }

  canUndo(): boolean {
    return this.undoStack.length > 0
  }

  canRedo(): boolean {
    return this.redoStack.length > 0
  }

  /** プロジェクト切替など、Undo の対象が別物になる時に全消去する。 */
  clear(): void {
    this.undoStack = []
    this.redoStack = []
    this.batching = false
    this.batchPushed = false
  }

  /** テスト用: 現在の深さ。 */
  get undoDepth(): number {
    return this.undoStack.length
  }

  get redoDepth(): number {
    return this.redoStack.length
  }
}
